import React, { useState } from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import { Activity, Clock, CheckCircle2, AlertCircle, ChevronDown, ChevronRight, Filter } from 'lucide-react';

export const TelemetryFeed: React.FC = () => {
  const { telemetryLogs } = useAutoFix();
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const toggleLog = (id: string) => {
    setExpandedLogId(expandedLogId === id ? null : id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Activity size={15} style={{ color: 'var(--accent)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
            Real-Time WebMCP Telemetry Bus
          </span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {telemetryLogs.length} events captured
        </span>
      </div>

      {telemetryLogs.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            color: 'var(--text-3)',
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-md)',
            border: '1px dashed var(--border)',
            fontSize: 12,
          }}
        >
          No telemetry events recorded yet. Trigger a bug or test a tool to watch real-time events.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {telemetryLogs.map((log, idx) => {
            const isExpanded = expandedLogId === log.id;
            const timeStr = new Date(log.timestamp).toLocaleTimeString();

            return (
              <div
                key={`${log.id}-${idx}`}
                className="card"
                style={{
                  padding: '8px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  fontSize: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {log.status === 'completed' ? (
                      <CheckCircle2 size={13} style={{ color: 'var(--green)' }} />
                    ) : (
                      <AlertCircle size={13} style={{ color: 'var(--red)' }} />
                    )}

                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-1)' }}>
                      {log.toolName}
                    </span>

                    <span className="badge badge-blue" style={{ fontSize: 9 }}>
                      {log.caller}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {log.executionTimeMs !== undefined && (
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: 'var(--font-mono)',
                          color: 'var(--accent)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                        }}
                      >
                        <Clock size={10} /> {log.executionTimeMs}ms
                      </span>
                    )}

                    <span style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                      {timeStr}
                    </span>

                    <button
                      type="button"
                      onClick={() => toggleLog(log.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-3)',
                        cursor: 'pointer',
                        padding: 2,
                      }}
                    >
                      {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: 8,
                      background: 'var(--bg-input)',
                      borderRadius: 4,
                      border: '1px solid var(--border)',
                      fontSize: 11,
                      fontFamily: 'var(--font-mono)',
                      overflowX: 'auto',
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--text-3)' }}>Input:</span>
                      <pre style={{ margin: '2px 0 6px', color: 'var(--text-2)' }}>
                        {JSON.stringify(log.input, null, 2)}
                      </pre>
                    </div>

                    {log.output && (
                      <div>
                        <span style={{ color: 'var(--accent)' }}>Output:</span>
                        <pre style={{ margin: '2px 0 0', color: 'var(--text-1)' }}>
                          {JSON.stringify(log.output, null, 2)}
                        </pre>
                      </div>
                    )}

                    {log.error && (
                      <div>
                        <span style={{ color: 'var(--red)' }}>Error:</span>
                        <pre style={{ margin: '2px 0 0', color: 'var(--red)' }}>{log.error}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
